import { DateTime, Interval } from 'luxon';
import { Injectable, inject } from '@angular/core';
import { BaseJob, JobPerform, JobType, OnTheWay } from '../types/job.type';
import { NonServicePeriodActionEnum, NonServicePeriodTypeEnum } from '../types/non-service-period.type';

import { AppData } from '../data';
import { DispatcherService } from './dispatcher.service';
import { LocationUtilityService } from './location-utility.service';
import { Route } from '../types/route.type';
import { RouteUpdate } from '../types/route-update.type';
import { environment } from 'src/environments/environment';
import { ScheduledEvent } from '../types/group.type';
import { combineLatest, map, shareReplay } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RouteProviderService {
  private dispatcherService = inject(DispatcherService);

  private readonly data = inject(AppData);

  getNoShowOptionList(job: BaseJob) {
    if (job.supportsDuplicatingNoShow) {
      return this.data.noShowOptions;
    } else {
      return this.data.noShowOptions.filter(x => !x.createsCopyOfRide);
    }
  }

  public readonly canCompleteRoute$ = combineLatest([
    this.data.route$,
    this.data.currentRouteIsPreview$,
  ]).pipe(
    map(([{ preTrip, postTrip, customForm, customFormCompleted, jobs, break: _break } = <any>{}, isPreview]) => {
      const stillWorkToDo =
            (preTrip && !preTrip.hasBeenCompleted)
        || (postTrip && !postTrip.hasBeenCompleted)
        || (customForm?.length > 0 && !customFormCompleted)
        || (_break && !_break.hasBeenCompleted)
        || jobs?.some(j => !j.completed);
      return !isPreview && !stillWorkToDo;
    }),
    shareReplay(1),
  );

  pickupsRemaining() {
    return this.data.route.jobs?.filter(job => job.jobType === JobType.Pickup).length;
  }

  setRouteForPreview(route: Route): void {
    this.data.set('route', route);
    this.data.set('currentRouteIsPreview', true);
  }

  async startRoute(routeUpdate: RouteUpdate, inspectionStartedDate: Date = null) {
    this.data.set('route', routeUpdate.route);
    this.data.set('driverIsActivelyReporting', true);
    this.data.set('noShowOptions', routeUpdate.noShowOptions);
    if (this.data.route.preTrip && !this.data.route.preTrip.hasBeenStarted && !this.data.route.preTrip.hasBeenCompleted) {
      this.data.set('route', { ...this.data.route, preTrip: { ...this.data.route.preTrip, hasBeenStarted: true }});
      await this.startNonServicePeriod(NonServicePeriodTypeEnum.PreTrip, inspectionStartedDate);
    }
  }

  async reportJobsOnTheWay({ onTheWays }: { onTheWays: OnTheWay[] }): Promise<void> {
    //  handle on the jobs
    if (onTheWays?.length) {
      await this.dispatcherService.onTheWays(onTheWays);
      const jobUpdates = onTheWays.map(onTheWay => {
        const match = this.data.route.jobs.find(j => j.rideId === onTheWay.rideId && j.jobType === JobType.Pickup);
        if (!match) { return null; }
        return { ...match, estimatedTimeOfArrival: onTheWay.estimatedTimeOfArrival, onTheWayTime: onTheWay.gpsReport.occuranceDate };
      }).filter(job => !!job);
      this.data.setJobs(jobUpdates.map(value => ({
        ...value,
        onTheWayTime: typeof value.onTheWayTime === 'string' ? value.onTheWayTime : value.onTheWayTime.toISOString(),
        estimatedTimeOfArrival: value.estimatedTimeOfArrival?.toISOString(),
      })));
    }
  }

  async performJob(jobPerform: JobPerform) {
    return this.dispatcherService.performJob(jobPerform);
  }

  async startNonServicePeriod(type: NonServicePeriodTypeEnum, inspectionStartedDate: Date = new Date()): Promise<void> {
    await this.dispatcherService.performNonServicePeriod(NonServicePeriodActionEnum.Start, type, inspectionStartedDate);
  }

  async finishNonServicePeriod(type: NonServicePeriodTypeEnum): Promise<void> {
    await this.dispatcherService.performNonServicePeriod(NonServicePeriodActionEnum.End, type);
  }

  public getSimilarJobs({ can, job }: { can: (job: BaseJob) => boolean, job: BaseJob }): ScheduledEvent<BaseJob>[] {
    const { timeTolerance, distanceTolerance } = environment.features;

    return this.data.route.jobs.filter(otherJob => {
      const interval = Interval.fromDateTimes(
        DateTime.fromJSDate(new Date(otherJob.scheduledTime)),
        DateTime.fromJSDate(new Date(job.scheduledTime)),
      );
      const timeDiff = interval?.isValid ? interval.count('minutes') : Infinity;

      const distanceBetween =
        !job.location.coordinate
        || !otherJob.location.coordinate
              ? null
              : LocationUtilityService.euclideanDistanceInMiles(
                  otherJob.location.coordinate.latitude,
                  otherJob.location.coordinate.longitude,
                  job.location.coordinate.latitude,
                  job.location.coordinate.longitude,
                );

             // it's not the job we are already working on
      return job.rideId !== otherJob.rideId
          // otherJob is not a will call
          && !otherJob.willCall
          // job are of the same type (pickup, dropoff)
          && job.jobType === otherJob.jobType
          // has a scheduled time
          && otherJob.scheduledTime
          // we are able to calculate the distance because both are geo-coded
          && distanceBetween != null
          //  we are close enough -- distance
          && distanceBetween <= distanceTolerance
          // we are close enough -- time
          && timeDiff <= timeTolerance
          // we are allowed to perform action on job (ex: canArrive, canArrive...)
          && can(otherJob);
    });
  }

  /** NEW STUFF */

}
